import React, { useState } from 'react';
import {
  Show,
  TextField,
  NumberField,
  BooleanField,
  DateField,
  FunctionField,
  useRecordContext,
  TabbedShowLayout,
  Tab,
  ReferenceField,
  ReferenceManyField,
  Datagrid,
  EditButton,
  TopToolbar,
  useRefresh,
  useNotify,
  useRedirect,
} from 'react-admin';
import {
  Box,
  Typography,
  Grid,
  Chip,
  Card,
  CardContent,
  IconButton,
  Button as MuiButton,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Divider,
} from '@mui/material';
import {
  Build as MaintenanceIcon,
  LocalGasStation as FuelIcon,
  Assignment as InspectionIcon,
  PersonAdd as AssignmentIcon,
  Warning as IncidentIcon,
  Info as InfoIcon,
  Add as CreateIcon,
  Edit as EditIcon,
  LocalShipping as VehicleIcon,
  PhotoCamera as PhotoIcon,
  Upload as UploadIcon,
  Close as CloseIcon,
  InsertDriveFile as InsertDriveFileIcon,
  Work as WorkIcon,
  Inventory as InventoryIcon,
  Assignment as TaskIcon,
  Add as AddIcon,
} from '@mui/icons-material';
import {
  MaintenanceDialog,
  FuelDialog,
  InspectionDialog,
  AssignmentDialog,
  IncidentDialog,
} from '../../components/dialogs';
import ResourceBreadcrumbs from '../../components/ResourceBreadcrumbs';
import { tenantService } from '../../services/tenantService';

// Safe wrapper for TextField to ensure string values
const SafeTextField = ({ source }: any) => {
  const record = useRecordContext();
  const value = record?.[source];

  if (!value) {
    return <span />;
  }

  // Ensure the value is a string
  const stringValue = typeof value === 'object' ? JSON.stringify(value) : String(value);

  return <span>{stringValue}</span>;
};

// Safe wrapper for NumberField to handle Decimal.js objects
const SafeNumberField = ({ source }: any) => {
  const record = useRecordContext();
  const value = record?.[source];

  if (!value) {
    return <span />;
  }

  // Handle Decimal.js objects (which have s, e, d properties)
  if (
    typeof value === 'object' &&
    value.s !== undefined &&
    value.e !== undefined &&
    value.d !== undefined
  ) {
    // Convert Decimal object to number
    const numberValue = Number(value);
    return <span>{isNaN(numberValue) ? '' : numberValue}</span>;
  }

  // Handle regular numbers
  if (typeof value === 'number') {
    return <span>{value}</span>;
  }

  // Try to convert string to number
  const numberValue = Number(value);
  return <span>{isNaN(numberValue) ? '' : numberValue}</span>;
};

// Job Types Section Component (View Only)
const JobTypesSection = () => {
  const record = useRecordContext();

  const utilizations = record?.utilizations || [];

  return (
    <Card sx={{ mb: 3, border: '1px solid #e0e0e0', boxShadow: '0 1px 2px rgba(0,0,0,0.05)' }}>
      <CardContent>
        <Box sx={{ display: 'flex', alignItems: 'center', mb: 3 }}>
          <WorkIcon sx={{ fontSize: 28, color: '#1976d2', mr: 2 }} />
          <Typography variant="h5" fontWeight="bold" color="primary">
            Job Types
          </Typography>
        </Box>

        {utilizations.length > 0 ? (
          <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1 }}>
            {utilizations.map((item: any, index: number) => (
              <Chip
                key={item.id || index}
                label={item.utilization?.name || item.name || 'Unknown'}
                color="primary"
                variant="outlined"
                sx={{ mb: 1 }}
              />
            ))}
          </Box>
        ) : (
          <Box
            sx={{
              textAlign: 'center',
              p: 3,
              backgroundColor: '#fafafa',
              borderRadius: 2,
              border: '1px solid #e0e0e0',
            }}
          >
            <WorkIcon sx={{ fontSize: 48, color: '#e0e0e0', mb: 1 }} />
            <Typography variant="body1" color="text.secondary">
              No job types assigned
            </Typography>
            <Typography variant="caption" color="text.secondary">
              Add job types in the edit view
            </Typography>
          </Box>
        )}
      </CardContent>
    </Card>
  );
};

// Commodities Section Component (View Only)
const CommoditiesSection = () => {
  const record = useRecordContext();

  const commodities = record?.commodities || [];

  return (
    <Card sx={{ mb: 3, border: '1px solid #e0e0e0', boxShadow: '0 1px 2px rgba(0,0,0,0.05)' }}>
      <CardContent>
        <Box sx={{ display: 'flex', alignItems: 'center', mb: 3 }}>
          <InventoryIcon sx={{ fontSize: 28, color: '#1976d2', mr: 2 }} />
          <Typography variant="h5" fontWeight="bold" color="primary">
            Commodities Hauled
          </Typography>
        </Box>

        {commodities.length > 0 ? (
          <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1 }}>
            {commodities.map((item: any, index: number) => (
              <Chip
                key={item.id || index}
                label={item.commodity?.name || item.name || 'Unknown'}
                color="secondary"
                variant="outlined"
                sx={{ mb: 1 }}
              />
            ))}
          </Box>
        ) : (
          <Box
            sx={{
              textAlign: 'center',
              p: 3,
              backgroundColor: '#fafafa',
              borderRadius: 2,
              border: '1px solid #e0e0e0',
            }}
          >
            <InventoryIcon sx={{ fontSize: 48, color: '#e0e0e0', mb: 1 }} />
            <Typography variant="body1" color="text.secondary">
              No commodities assigned
            </Typography>
            <Typography variant="caption" color="text.secondary">
              Add commodities in the edit view
            </Typography>
          </Box>
        )}
      </CardContent>
    </Card>
  );
};

// Document Upload Dialog Component (similar to PhotoUploadDialog)
const DocumentUploadDialog = ({
  open,
  onClose,
  vehicleId,
  currentFile,
  onFileUpdated,
  documentType,
  label,
}: {
  open: boolean;
  onClose: () => void;
  vehicleId: number;
  currentFile?: { url: string; name: string };
  onFileUpdated: (fileUrl: string, fileName: string) => void;
  documentType: 'ownershipPermit' | 'weightRating';
  label: string;
}) => {
  const [selectedFile, setSelectedFile] = useState<File | null>(null);
  const [previewUrl, setPreviewUrl] = useState<string>(currentFile?.url || '');
  const [uploading, setUploading] = useState(false);
  const [isDragOver, setIsDragOver] = useState(false);
  const notify = useNotify();
  const refresh = useRefresh();

  const validateFile = (file: File): boolean => {
    // Validate file size (max 10MB for documents)
    if (file.size > 10 * 1024 * 1024) {
      notify('File size must be less than 10MB', { type: 'error' });
      return false;
    }

    return true;
  };

  const handleFileSelect = (file: File) => {
    if (!validateFile(file)) {
      return;
    }

    setSelectedFile(file);
    const reader = new FileReader();
    reader.onload = e => {
      setPreviewUrl(e.target?.result as string);
    };
    reader.readAsDataURL(file);
  };

  const handleInputChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    if (file) {
      handleFileSelect(file);
    }
  };

  const handleDragOver = (e: React.DragEvent) => {
    e.preventDefault();
    setIsDragOver(true);
  };

  const handleDragLeave = (e: React.DragEvent) => {
    e.preventDefault();
    setIsDragOver(false);
  };

  const handleDrop = (e: React.DragEvent) => {
    e.preventDefault();
    setIsDragOver(false);

    const files = e.dataTransfer.files;
    if (files.length > 0) {
      handleFileSelect(files[0]);
    }
  };

  const uploadToServer = async (file: File): Promise<any> => {
    const formData = new FormData();
    formData.append('file', file);
    formData.append('name', `equipment-${vehicleId}-${documentType}`);
    formData.append('description', `${label} for equipment ${vehicleId}`);
    formData.append('folder', 'equipment-documents');
    formData.append('isPublic', 'true');

    const baseUrl = import.meta.env.VITE_SIMPLE_REST_URL;
    const accessToken = localStorage.getItem('accessToken');

    const response = await fetch(`${baseUrl}/files`, {
      method: 'POST',
      headers: {
        Authorization: `Bearer ${accessToken}`,
      },
      body: formData,
    });

    if (!response.ok) {
      throw new Error(`Upload failed: ${response.statusText}`);
    }

    return response.json();
  };

  const updateEquipmentDocument = async (fileId: number) => {
    const baseUrl = import.meta.env.VITE_SIMPLE_REST_URL;
    const accessToken = localStorage.getItem('accessToken');

    const fieldName =
      documentType === 'ownershipPermit' ? 'ownershipPermitFileId' : 'weightRatingFileId';

    const response = await fetch(`${baseUrl}/equipment/${vehicleId}`, {
      method: 'PATCH',
      headers: {
        Authorization: `Bearer ${accessToken}`,
        'Content-Type': 'application/json',
      },
      body: JSON.stringify({
        [fieldName]: fileId,
      }),
    });

    if (!response.ok) {
      throw new Error(`Failed to update equipment: ${response.statusText}`);
    }

    return response.json();
  };

  const handleUpload = async () => {
    if (!selectedFile) {
      return;
    }

    setUploading(true);
    try {
      // Upload file to server
      const fileData = await uploadToServer(selectedFile);

      // Update equipment with document file ID
      await updateEquipmentDocument(fileData.id);

      // Update UI
      onFileUpdated(fileData.url, fileData.name);
      notify(`${label} uploaded successfully`, { type: 'success' });
      refresh();
      onClose();
    } catch (error: any) {
      notify(`Failed to upload ${label}: ${error.message}`, { type: 'error' });
    } finally {
      setUploading(false);
    }
  };

  const handleRemoveDocument = async () => {
    setUploading(true);
    try {
      const fieldName =
        documentType === 'ownershipPermit' ? 'ownershipPermitFileId' : 'weightRatingFileId';

      const baseUrl = import.meta.env.VITE_SIMPLE_REST_URL;
      const accessToken = localStorage.getItem('accessToken');

      const response = await fetch(`${baseUrl}/equipment/${vehicleId}`, {
        method: 'PATCH',
        headers: {
          Authorization: `Bearer ${accessToken}`,
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          [fieldName]: null,
        }),
      });

      if (!response.ok) {
        throw new Error(`Failed to update equipment: ${response.statusText}`);
      }

      setSelectedFile(null);
      setPreviewUrl('');
      onFileUpdated('', '');
      notify(`${label} removed`, { type: 'success' });
      refresh();
      onClose();
    } catch (error: any) {
      notify(`Failed to remove ${label}: ${error.message}`, { type: 'error' });
    } finally {
      setUploading(false);
    }
  };

  const isImage = (filename: string) => {
    return /\.(jpg|jpeg|png|gif|svg|webp)$/i.test(filename);
  };

  return (
    <Dialog open={open} onClose={onClose} maxWidth="sm" fullWidth>
      <DialogTitle>
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
          {label}
          <IconButton onClick={onClose}>
            <CloseIcon />
          </IconButton>
        </Box>
      </DialogTitle>
      <DialogContent>
        <Box sx={{ textAlign: 'center', py: 2 }}>
          {previewUrl ? (
            isImage(selectedFile?.name || currentFile?.name || '') ? (
              <Box
                component="img"
                src={previewUrl}
                alt={label}
                sx={{
                  maxWidth: '100%',
                  maxHeight: 300,
                  borderRadius: 2,
                  mb: 2,
                }}
              />
            ) : (
              <Box sx={{ mb: 2, p: 3, backgroundColor: '#f5f5f5', borderRadius: 2 }}>
                <InsertDriveFileIcon sx={{ fontSize: 64, color: '#1976d2', mb: 1 }} />
                <Typography variant="body1">{selectedFile?.name || currentFile?.name}</Typography>
              </Box>
            )
          ) : (
            <Box
              onDragOver={handleDragOver}
              onDragLeave={handleDragLeave}
              onDrop={handleDrop}
              onClick={() => document.getElementById(`${documentType}-file-input`)?.click()}
              sx={{
                width: '100%',
                height: 200,
                border: isDragOver ? '2px dashed #1976d2' : '2px dashed #ccc',
                borderRadius: 2,
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                mb: 2,
                backgroundColor: isDragOver ? '#f3f7ff' : '#f9f9f9',
                cursor: 'pointer',
                transition: 'all 0.2s ease',
                '&:hover': {
                  borderColor: '#1976d2',
                  backgroundColor: '#f3f7ff',
                },
              }}
            >
              <Box sx={{ textAlign: 'center', color: 'text.secondary' }}>
                <UploadIcon
                  sx={{ fontSize: 48, mb: 1, color: isDragOver ? '#1976d2' : 'inherit' }}
                />
                <Typography variant="body2" sx={{ color: isDragOver ? '#1976d2' : 'inherit' }}>
                  {isDragOver ? 'Drop file here' : 'Drag and drop file here or click to select'}
                </Typography>
              </Box>
            </Box>
          )}

          <input
            accept="image/*,.pdf,.doc,.docx"
            style={{ display: 'none' }}
            id={`${documentType}-file-input`}
            type="file"
            onChange={handleInputChange}
          />
          <label htmlFor={`${documentType}-file-input`}>
            <MuiButton
              variant="outlined"
              component="span"
              startIcon={<InsertDriveFileIcon />}
              sx={{ mr: 1 }}
            >
              Select File
            </MuiButton>
          </label>

          {currentFile && (
            <MuiButton
              variant="outlined"
              color="error"
              onClick={handleRemoveDocument}
              sx={{ ml: 1 }}
            >
              Remove Current {label}
            </MuiButton>
          )}

          <Typography variant="caption" display="block" color="text.secondary" sx={{ mt: 2 }}>
            Maximum file size: 10MB • Supported formats: Images, PDF, DOC, DOCX
          </Typography>
        </Box>
      </DialogContent>
      <DialogActions>
        <MuiButton onClick={onClose}>Cancel</MuiButton>
        <MuiButton variant="contained" onClick={handleUpload} disabled={!selectedFile || uploading}>
          {uploading ? 'Uploading...' : 'Upload'}
        </MuiButton>
      </DialogActions>
    </Dialog>
  );
};

// Document Display Component (similar to vehicle photo display)
const DocumentDisplay = ({
  file,
  label,
  documentType,
  vehicleId,
}: {
  file?: { url: string; name: string };
  label: string;
  documentType: 'ownershipPermit' | 'weightRating';
  vehicleId: number;
}) => {
  const [dialogOpen, setDialogOpen] = useState(false);
  const [currentFile, setCurrentFile] = useState(file);

  React.useEffect(() => {
    setCurrentFile(file);
  }, [file]);

  const handleFileUpdated = (fileUrl: string, fileName: string) => {
    if (fileUrl && fileName) {
      setCurrentFile({ url: fileUrl, name: fileName });
    } else {
      setCurrentFile(undefined);
    }
  };

  const isImage = (filename?: string) => {
    if (!filename) {
      return false;
    }
    return /\.(jpg|jpeg|png|gif|svg|webp)$/i.test(filename);
  };

  return (
    <>
      <Box
        sx={{
          width: '100%',
          height: 250,
          borderRadius: 2,
          overflow: 'hidden',
          backgroundColor: '#f5f5f5',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          cursor: 'pointer',
          border: '2px dashed #ddd',
          '&:hover': {
            backgroundColor: '#f0f0f0',
            borderColor: '#1976d2',
          },
        }}
        onClick={() => setDialogOpen(true)}
      >
        {currentFile?.url ? (
          isImage(currentFile.name) ? (
            <Box
              component="img"
              src={currentFile.url}
              alt={label}
              sx={{
                width: '100%',
                height: '100%',
                objectFit: 'contain',
              }}
            />
          ) : (
            <Box sx={{ textAlign: 'center', color: 'text.secondary' }}>
              <InsertDriveFileIcon sx={{ fontSize: 64, mb: 1, color: '#1976d2' }} />
              <Typography variant="body2" sx={{ px: 2 }} noWrap>
                {currentFile.name}
              </Typography>
              <Typography variant="caption">Click to view or replace</Typography>
            </Box>
          )
        ) : (
          <Box sx={{ textAlign: 'center', color: 'text.secondary' }}>
            <InsertDriveFileIcon sx={{ fontSize: 48, mb: 1 }} />
            <Typography variant="body2">Click to upload {label.toLowerCase()}</Typography>
          </Box>
        )}
      </Box>

      <DocumentUploadDialog
        open={dialogOpen}
        onClose={() => setDialogOpen(false)}
        vehicleId={vehicleId}
        currentFile={currentFile}
        onFileUpdated={handleFileUpdated}
        documentType={documentType}
        label={label}
      />
    </>
  );
};

// Documents Section Component (View Only)
const DocumentsSectionViewOnly = () => {
  const record = useRecordContext();

  if (!record) {
    return null;
  }

  return (
    <Card sx={{ mb: 3, border: '1px solid #e0e0e0', boxShadow: '0 1px 2px rgba(0,0,0,0.05)' }}>
      <CardContent>
        <Box sx={{ display: 'flex', alignItems: 'center', mb: 3 }}>
          <InsertDriveFileIcon sx={{ fontSize: 28, color: '#1976d2', mr: 2 }} />
          <Typography variant="h5" fontWeight="bold" color="primary">
            Documents
          </Typography>
        </Box>
        <Grid container spacing={3}>
          <Grid item xs={12} md={6}>
            <Typography variant="subtitle2" gutterBottom sx={{ fontWeight: 600, mb: 2 }}>
              Ownership Permit
            </Typography>
            <DocumentDisplay
              file={record.ownershipPermitFile}
              fileId={record.ownershipPermitFileId}
              label="Ownership Permit"
              documentType="ownershipPermit"
              vehicleId={Number(record.id)}
            />
          </Grid>
          <Grid item xs={12} md={6}>
            <Typography variant="subtitle2" gutterBottom sx={{ fontWeight: 600, mb: 2 }}>
              Weight Rating Certificate
            </Typography>
            <DocumentDisplay
              file={record.weightRatingFile}
              label="Weight Rating Certificate"
              documentType="weightRating"
              vehicleId={Number(record.id)}
            />
          </Grid>
        </Grid>
      </CardContent>
    </Card>
  );
};

// Custom field components
const StatusField = () => {
  const record = useRecordContext();
  if (!record?.status) {
    return <span />;
  }

  const getColor = (status: string) => {
    switch (status) {
      case 'ACTIVE':
        return 'success';
      case 'MAINTENANCE':
        return 'warning';
      case 'INACTIVE':
        return 'default';
      case 'RETIRED':
        return 'info';
      case 'DELETED':
        return 'error';
      default:
        return 'default';
    }
  };

  return (
    <Chip
      label={String(record.status)}
      color={getColor(record.status)}
      size="small"
      variant="outlined"
    />
  );
};

const TypeField = () => {
  const record = useRecordContext();
  if (!record?.type) {
    return <span />;
  }

  return <Chip label={String(record.type).replace(/_/g, ' ')} color="primary" size="small" />;
};

const OwnershipField = () => {
  const record = useRecordContext();
  if (!record?.ownershipType) {
    return <span />;
  }

  return <Chip label={String(record.ownershipType)} color="info" size="small" variant="outlined" />;
};

const CurrencyField = ({ source }: { source: string }) => {
  const record = useRecordContext();
  const value = record?.[source];

  if (!value || isNaN(Number(value))) {
    return <span />;
  }

  return <span>${Number(value).toLocaleString()}</span>;
};

// Custom field for fuel type display
const FuelTypeField = () => {
  const record = useRecordContext();
  const value = record?.fuelType;

  const fuelTypeMap: Record<string, string> = {
    GASOLINE: 'Gasoline',
    DIESEL: 'Diesel',
    PROPANE: 'Propane',
    ELECTRIC: 'Electric',
    HYBRID: 'Hybrid',
  };

  return <span>{fuelTypeMap[value] || value || ''}</span>;
};

// Custom field for service type display
const ServiceTypeField = () => {
  const record = useRecordContext();
  const value = record?.serviceType;

  const serviceTypeMap: Record<string, string> = {
    OIL_CHANGE: 'Oil Change',
    INSPECTION: 'Inspection',
    REPAIR: 'Repair',
    TIRE_SERVICE: 'Tire Service',
    BRAKE_SERVICE: 'Brake Service',
    ENGINE_SERVICE: 'Engine Service',
    TRANSMISSION_SERVICE: 'Transmission Service',
    ELECTRICAL: 'Electrical',
    BODYWORK: 'Bodywork',
    PREVENTIVE: 'Preventive Maintenance',
    EMERGENCY: 'Emergency Repair',
    OTHER: 'Other',
  };

  return <span>{serviceTypeMap[value] || value || ''}</span>;
};

// Custom field for inspection type display
const InspectionTypeField = () => {
  const record = useRecordContext();
  const value = record?.inspectionType;

  const inspectionTypeMap: Record<string, string> = {
    ANNUAL: 'Annual Inspection',
    DAILY: 'Daily Inspection',
    WEEKLY: 'Weekly Inspection',
    MONTHLY: 'Monthly Inspection',
    PRE_TRIP: 'Pre-Trip Inspection',
    POST_TRIP: 'Post-Trip Inspection',
    DOT: 'DOT Inspection',
    CVOR: 'CVOR Inspection',
    SAFETY: 'Safety Inspection',
    EMISSIONS: 'Emissions Inspection',
    BRAKE: 'Brake Inspection',
    TIRE: 'Tire Inspection',
    ENGINE: 'Engine Inspection',
    ELECTRICAL: 'Electrical Inspection',
    OTHER: 'Other',
  };

  return <span>{inspectionTypeMap[value] || value || ''}</span>;
};

// Custom field for condition display in assignments
const ConditionField = () => {
  const record = useRecordContext();
  const value = record?.condition;

  const conditionMap: Record<string, string> = {
    EXCELLENT: 'Excellent',
    GOOD: 'Good',
    FAIR: 'Fair',
    POOR: 'Poor',
    DAMAGED: 'Damaged',
  };

  return <span>{conditionMap[value] || value || ''}</span>;
};

// Custom field for incident type display
const IncidentTypeField = () => {
  const record = useRecordContext();
  const value = record?.incidentType;

  const incidentTypeMap: Record<string, string> = {
    ACCIDENT: 'Accident',
    MINOR_ACCIDENT: 'Minor Accident',
    MAJOR_ACCIDENT: 'Major Accident',
    BREAKDOWN: 'Breakdown',
    MECHANICAL_FAILURE: 'Mechanical Failure',
    DAMAGE: 'Damage',
    THEFT: 'Theft',
    VANDALISM: 'Vandalism',
    FIRE: 'Fire',
    FLOOD: 'Flood',
    COLLISION: 'Collision',
    TRAFFIC_VIOLATION: 'Traffic Violation',
    OTHER: 'Other',
  };

  return <span>{incidentTypeMap[value] || value || ''}</span>;
};

// Custom field for driver name display with error handling
const DriverNameField = () => {
  const record = useRecordContext();

  if (!record?.driverId) {
    return <span />;
  }

  // If driver data is populated
  if (record.driver?.user?.name) {
    return <span>{record.driver.user.name}</span>;
  }

  // Fallback display
  return <span>Driver {record.driverId}</span>;
};

// Custom Edit Button Component
const CustomEditButton = ({ onClick }: { onClick: (id: string | number) => void }) => {
  const record = useRecordContext();

  return (
    <IconButton onClick={() => onClick(record?.id)} size="small" color="primary" title="Edit">
      <EditIcon />
    </IconButton>
  );
};

// Dialog Components - now imported from separate files

// Tab Components
const MaintenanceTab = () => {
  const record = useRecordContext();
  const [createDialogOpen, setCreateDialogOpen] = useState(false);
  const [editDialogOpen, setEditDialogOpen] = useState(false);
  const [editRecordId, setEditRecordId] = useState(null);

  const handleEdit = (id: string | number) => {
    setEditRecordId(Number(id));
    setEditDialogOpen(true);
  };

  return (
    <ReferenceManyField reference="equipment-maintenance" target="equipmentId" label={false}>
      <Box>
        <Box sx={{ mb: 2, display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
          <Typography variant="h6" color="primary">
            Maintenance Records
          </Typography>
          <MuiButton
            onClick={() => setCreateDialogOpen(true)}
            startIcon={<CreateIcon />}
            variant="contained"
            size="small"
          >
            Add Maintenance Record
          </MuiButton>
        </Box>
        <MaintenanceDialog
          open={createDialogOpen}
          onClose={() => setCreateDialogOpen(false)}
          equipmentId={Number(record?.id)}
        />
        <MaintenanceDialog
          open={editDialogOpen}
          onClose={() => setEditDialogOpen(false)}
          equipmentId={Number(record?.id)}
          recordId={editRecordId}
          isEdit={true}
        />
        <Datagrid
          bulkActionButtons={false}
          rowClick={(id) => {
            handleEdit(id);
            return false;
          }}
          sx={{
            '& .RaDatagrid-row': {
              cursor: 'pointer',
              '&:hover': {
                backgroundColor: 'rgba(0, 0, 0, 0.04)',
              },
            },
          }}
          empty={
            <Box sx={{ p: 4, textAlign: 'center' }}>
              <MaintenanceIcon sx={{ fontSize: 48, color: 'text.disabled', mb: 2 }} />
              <Typography variant="body1" color="text.secondary" gutterBottom>
                No maintenance records yet
              </Typography>
              <Typography variant="body2" color="text.secondary">
                Start tracking vehicle maintenance by adding your first service record
              </Typography>
            </Box>
          }
        >
          <DateField source="serviceDate" label="Service Date" />
          <ServiceTypeField />
          <ReferenceField
            source="serviceProviderId"
            reference="service-providers"
            label="Service Provider"
            emptyText="No Provider"
            link={false}
          >
            <TextField source="name" />
          </ReferenceField>
          <SafeNumberField source="serviceMileage" label="Mileage" />
          <SafeNumberField source="totalCost" label="Total Cost ($)" />
          <BooleanField source="scheduledService" label="Scheduled" />
          <CustomEditButton onClick={handleEdit} />
        </Datagrid>
      </Box>
    </ReferenceManyField>
  );
};

const FuelTab = () => {
  const record = useRecordContext();
  const [createDialogOpen, setCreateDialogOpen] = useState(false);
  const [editDialogOpen, setEditDialogOpen] = useState(false);
  const [editRecordId, setEditRecordId] = useState(null);

  const handleEdit = (id: string | number) => {
    setEditRecordId(Number(id));
    setEditDialogOpen(true);
  };

  return (
    <ReferenceManyField reference="equipment-fuel" target="equipmentId" label={false}>
      <Box>
        <Box sx={{ mb: 2, display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
          <Typography variant="h6" color="primary">
            Fuel Records
          </Typography>
          <MuiButton
            onClick={() => setCreateDialogOpen(true)}
            startIcon={<CreateIcon />}
            variant="contained"
            size="small"
          >
            Add Fuel Record
          </MuiButton>
        </Box>
        <FuelDialog
          open={createDialogOpen}
          onClose={() => setCreateDialogOpen(false)}
          equipmentId={record?.id}
        />
        <FuelDialog
          open={editDialogOpen}
          onClose={() => setEditDialogOpen(false)}
          equipmentId={record?.id}
          recordId={editRecordId}
          isEdit={true}
        />
        <Datagrid
          bulkActionButtons={false}
          rowClick={id => handleEdit(id)}
          sx={{
            '& .RaDatagrid-row': {
              cursor: 'pointer',
              '&:hover': {
                backgroundColor: 'rgba(0, 0, 0, 0.04)',
              },
            },
          }}
          empty={
            <Box sx={{ p: 4, textAlign: 'center' }}>
              <FuelIcon sx={{ fontSize: 48, color: 'text.disabled', mb: 2 }} />
              <Typography variant="body1" color="text.secondary" gutterBottom>
                No fuel records yet
              </Typography>
              <Typography variant="body2" color="text.secondary">
                Track vehicle fuel purchases and consumption to monitor efficiency
              </Typography>
            </Box>
          }
        >
          <DateField source="fuelDate" label="Fuel Date" />
          <FuelTypeField label="Fuel Type" />
          <SafeNumberField source="quantity" label="Quantity" />
          <SafeNumberField source="unitCost" label="Unit Cost ($)" />
          <SafeNumberField source="totalCost" label="Total Cost ($)" />
          <SafeNumberField source="mileage" label="Mileage" />
          <SafeTextField source="location" label="Location" />
          <CustomEditButton onClick={handleEdit} />
        </Datagrid>
      </Box>
    </ReferenceManyField>
  );
};

const InspectionsTab = () => {
  const record = useRecordContext();
  const [createDialogOpen, setCreateDialogOpen] = useState(false);
  const [editDialogOpen, setEditDialogOpen] = useState(false);
  const [editRecordId, setEditRecordId] = useState(null);

  const handleEdit = (id: string | number) => {
    setEditRecordId(Number(id));
    setEditDialogOpen(true);
  };

  return (
    <ReferenceManyField reference="equipment-inspections" target="equipmentId" label={false}>
      <Box>
        <Box sx={{ mb: 2, display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
          <Typography variant="h6" color="primary">
            Inspections
          </Typography>
          <MuiButton
            onClick={() => setCreateDialogOpen(true)}
            startIcon={<CreateIcon />}
            variant="contained"
            size="small"
          >
            Add Inspection
          </MuiButton>
        </Box>
        <InspectionDialog
          open={createDialogOpen}
          onClose={() => setCreateDialogOpen(false)}
          equipmentId={record?.id}
        />
        <InspectionDialog
          open={editDialogOpen}
          onClose={() => setEditDialogOpen(false)}
          equipmentId={record?.id}
          recordId={editRecordId}
          isEdit={true}
        />
        <Datagrid
          bulkActionButtons={false}
          rowClick={id => handleEdit(id)}
          sx={{
            '& .RaDatagrid-row': {
              cursor: 'pointer',
              '&:hover': {
                backgroundColor: 'rgba(0, 0, 0, 0.04)',
              },
            },
          }}
          empty={
            <Box sx={{ p: 4, textAlign: 'center' }}>
              <InspectionIcon sx={{ fontSize: 48, color: 'text.disabled', mb: 2 }} />
              <Typography variant="body1" color="text.secondary" gutterBottom>
                No inspections yet
              </Typography>
              <Typography variant="body2" color="text.secondary">
                Stay compliant by recording vehicle safety inspections and checks
              </Typography>
            </Box>
          }
        >
          <DateField source="inspectionDate" label="Inspection Date" />
          <InspectionTypeField label="Inspection Type" />
          <SafeTextField source="inspector" label="Inspector" />
          <BooleanField source="passed" label="Passed" />
          <DateField source="nextDueDate" label="Next Due Date" />
          <CustomEditButton onClick={handleEdit} />
        </Datagrid>
      </Box>
    </ReferenceManyField>
  );
};

const AssignmentsTab = () => {
  const record = useRecordContext();
  const [createDialogOpen, setCreateDialogOpen] = useState(false);
  const [editDialogOpen, setEditDialogOpen] = useState(false);
  const [editRecordId, setEditRecordId] = useState(null);

  const handleEdit = (id: string | number) => {
    setEditRecordId(Number(id));
    setEditDialogOpen(true);
  };

  return (
    <ReferenceManyField reference="equipment-assignments" target="equipmentId" label={false}>
      <Box>
        <Box sx={{ mb: 2, display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
          <Typography variant="h6" color="primary">
            Assignment History
          </Typography>
          <MuiButton
            onClick={() => setCreateDialogOpen(true)}
            startIcon={<CreateIcon />}
            variant="contained"
            size="small"
          >
            Add Assignment
          </MuiButton>
        </Box>
        <AssignmentDialog
          open={createDialogOpen}
          onClose={() => setCreateDialogOpen(false)}
          equipmentId={record?.id}
        />
        <AssignmentDialog
          open={editDialogOpen}
          onClose={() => setEditDialogOpen(false)}
          equipmentId={record?.id}
          recordId={editRecordId}
          isEdit={true}
        />
        <Datagrid
          bulkActionButtons={false}
          rowClick={id => handleEdit(id)}
          sx={{
            '& .RaDatagrid-row': {
              cursor: 'pointer',
              '&:hover': {
                backgroundColor: 'rgba(0, 0, 0, 0.04)',
              },
            },
          }}
          empty={
            <Box sx={{ p: 4, textAlign: 'center' }}>
              <AssignmentIcon sx={{ fontSize: 48, color: 'text.disabled', mb: 2 }} />
              <Typography variant="body1" color="text.secondary" gutterBottom>
                No assignments yet
              </Typography>
              <Typography variant="body2" color="text.secondary">
                Track who uses this vehicle and when
              </Typography>
            </Box>
          }
        >
          <DriverNameField label="Driver" />
          <ConditionField label="Condition" />
          <DateField source="assignedDate" label="Assigned Date" />
          <NumberField source="mileageOut" label="Mileage Out" />
          <DateField source="returnedDate" label="Returned Date" />
          <NumberField source="mileageIn" label="Mileage In" />
          <SafeTextField source="notes" label="Notes" />
          <CustomEditButton onClick={handleEdit} />
        </Datagrid>
      </Box>
    </ReferenceManyField>
  );
};

const IncidentsTab = () => {
  const record = useRecordContext();
  const [createDialogOpen, setCreateDialogOpen] = useState(false);
  const [editDialogOpen, setEditDialogOpen] = useState(false);
  const [editRecordId, setEditRecordId] = useState(null);

  const handleEdit = (id: string | number) => {
    setEditRecordId(Number(id));
    setEditDialogOpen(true);
  };

  return (
    <ReferenceManyField reference="equipment-incidents" target="equipmentId" label={false}>
      <Box>
        <Box sx={{ mb: 2, display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
          <Typography variant="h6" color="primary">
            Incident Reports
          </Typography>
          <MuiButton
            onClick={() => setCreateDialogOpen(true)}
            startIcon={<CreateIcon />}
            variant="contained"
            size="small"
          >
            Add Incident
          </MuiButton>
        </Box>
        <IncidentDialog
          open={createDialogOpen}
          onClose={() => setCreateDialogOpen(false)}
          equipmentId={record?.id}
        />
        <IncidentDialog
          open={editDialogOpen}
          onClose={() => setEditDialogOpen(false)}
          equipmentId={record?.id}
          recordId={editRecordId}
          isEdit={true}
        />
        <Datagrid
          bulkActionButtons={false}
          rowClick={id => handleEdit(id)}
          sx={{
            '& .RaDatagrid-row': {
              cursor: 'pointer',
              '&:hover': {
                backgroundColor: 'rgba(0, 0, 0, 0.04)',
              },
            },
          }}
          empty={
            <Box sx={{ p: 4, textAlign: 'center' }}>
              <IncidentIcon sx={{ fontSize: 48, color: 'text.disabled', mb: 2 }} />
              <Typography variant="body1" color="text.secondary" gutterBottom>
                No incidents reported
              </Typography>
              <Typography variant="body2" color="text.secondary">
                Document any accidents, damages, or issues with this vehicle
              </Typography>
            </Box>
          }
        >
          <DateField source="incidentDate" label="Incident Date" />
          <IncidentTypeField label="Incident Type" />
          <SafeTextField source="description" label="Description" />
          <DriverNameField label="Driver" />
          <NumberField source="repairCost" label="Repair Cost ($)" />
          <BooleanField source="resolved" label="Resolved" />
          <CustomEditButton onClick={handleEdit} />
        </Datagrid>
      </Box>
    </ReferenceManyField>
  );
};

// Custom chip components for task status and priority
const TaskStatusChip = ({ record }: { record: any }) => {
  if (!record?.status) {return <span>-</span>;}
  
  const getStatusColor = (status: string) => {
    const statusColors: Record<string, string> = {
      Todo: '#6b7280',
      InProgress: '#3b82f6', 
      InReview: '#f59e0b',
      Blocked: '#ef4444',
      Done: '#10b981',
      Cancelled: '#9ca3af',
    };
    return statusColors[status] || '#6b7280';
  };

  return (
    <Chip
      label={record.status}
      sx={{
        backgroundColor: getStatusColor(record.status),
        color: 'white',
        fontWeight: 'bold',
        fontSize: '0.75rem'
      }}
      size="small"
    />
  );
};

const TaskPriorityChip = ({ record }: { record: any }) => {
  if (!record?.priority) {return <span>-</span>;}
  
  const getPriorityColor = (priority: string) => {
    const priorityColors: Record<string, string> = {
      Low: '#10b981',
      Medium: '#f59e0b',
      High: '#ef4444',
      Urgent: '#dc2626',
    };
    return priorityColors[priority] || '#6b7280';
  };

  return (
    <Chip
      label={record.priority}
      sx={{
        backgroundColor: getPriorityColor(record.priority),
        color: 'white',
        fontWeight: 'bold',
        fontSize: '0.75rem'
      }}
      size="small"
    />
  );
};

const TasksTabContent = () => {
  const record = useRecordContext();
  const redirect = useRedirect();

  return (
    <Card>
      <CardContent>
        <Typography variant="h6" gutterBottom sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          <TaskIcon color="primary" />
          Tasks
          <MuiButton
            variant="contained"
            size="small"
            startIcon={<AddIcon />}
            sx={{ ml: 'auto' }}
            onClick={() => redirect(`/tasks/create?entityType=equipment&entityId=${record?.id}`)}
          >
            Create Task
          </MuiButton>
        </Typography>
        <Divider sx={{ mb: 2 }} />
        <ReferenceManyField
          reference="tasks"
          target="entityId"
          filter={{ entityType: 'equipment' }}
          sort={{ field: 'priority', order: 'DESC' }}
          perPage={25}
        >
          <Datagrid bulkActionButtons={false} rowClick="show">
            <TextField source="title" label="Task" />
            <FunctionField
              source="status"
              label="Status"
              render={(record: any) => <TaskStatusChip record={record} />}
            />
            <FunctionField
              source="priority"
              label="Priority"
              render={(record: any) => <TaskPriorityChip record={record} />}
            />
            <TextField source="assignee.name" label="Assignee" />
            <DateField source="dueDate" label="Due Date" />
            <DateField source="createdAt" label="Created" />
          </Datagrid>
        </ReferenceManyField>
      </CardContent>
    </Card>
  );
};

const TasksTab = () => {
  return <TasksTabContent />;
};

const EquipmentShowActions = () => (
  <TopToolbar>
    <EditButton />
  </TopToolbar>
);

// Tab Label with Count Component
const TabLabelWithCount = ({ title, color }: { title: string; color: string }) => {
  const record = useRecordContext();

  // You would typically get these counts from the data
  // For now, we'll use placeholder logic
  const getCount = (tabType: string) => {
    // This would be replaced with actual count logic
    // based on the related data for each tab
    switch (tabType.toLowerCase()) {
      case 'maintenance':
        return record?.maintenanceCount || 0;
      case 'fuel records':
        return record?.fuelCount || 0;
      case 'inspections':
        return record?.inspectionCount || 0;
      case 'assignments':
        return record?.assignmentCount || 0;
      case 'incidents':
        return record?.incidentCount || 0;
      default:
        return 0;
    }
  };

  const count = getCount(title);

  return (
    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
      <span>{title}</span>
      {count > 0 && (
        <Chip
          label={count}
          size="small"
          sx={{
            backgroundColor: color,
            color: 'white',
            fontWeight: 'bold',
            minWidth: '20px',
            height: '20px',
            '& .MuiChip-label': {
              padding: '0 6px',
              fontSize: '0.75rem',
            },
          }}
        />
      )}
    </Box>
  );
};

// Photo Upload Dialog Component
const PhotoUploadDialog = ({
  open,
  onClose,
  vehicleId,
  currentPhoto,
  onPhotoUpdated,
}: {
  open: boolean;
  onClose: () => void;
  vehicleId: number;
  currentPhoto?: string;
  onPhotoUpdated: (photoUrl: string) => void;
}) => {
  const [selectedFile, setSelectedFile] = useState<File | null>(null);
  const [previewUrl, setPreviewUrl] = useState<string>(currentPhoto || '');
  const [uploading, setUploading] = useState(false);
  const [isDragOver, setIsDragOver] = useState(false);
  const notify = useNotify();
  const refresh = useRefresh();

  const validateFile = (file: File): boolean => {
    // Validate file size (max 5MB)
    if (file.size > 5 * 1024 * 1024) {
      notify('File size must be less than 5MB', { type: 'error' });
      return false;
    }

    // Validate file type
    if (!file.type.startsWith('image/')) {
      notify('Please select an image file', { type: 'error' });
      return false;
    }

    return true;
  };

  const handleFileSelect = (file: File) => {
    if (!validateFile(file)) {
      return;
    }

    setSelectedFile(file);
    const reader = new FileReader();
    reader.onload = e => {
      setPreviewUrl(e.target?.result as string);
    };
    reader.readAsDataURL(file);
  };

  const handleInputChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    if (file) {
      handleFileSelect(file);
    }
  };

  const handleDragOver = (e: React.DragEvent) => {
    e.preventDefault();
    setIsDragOver(true);
  };

  const handleDragLeave = (e: React.DragEvent) => {
    e.preventDefault();
    setIsDragOver(false);
  };

  const handleDrop = (e: React.DragEvent) => {
    e.preventDefault();
    setIsDragOver(false);

    const files = e.dataTransfer.files;
    if (files.length > 0) {
      handleFileSelect(files[0]);
    }
  };

  const uploadToServer = async (file: File): Promise<any> => {
    const formData = new FormData();
    formData.append('file', file);
    formData.append('name', `equipment-${vehicleId}-photo`);
    formData.append('description', `Primary photo for equipment ${vehicleId}`);
    formData.append('folder', 'equipment-photos');
    formData.append('isPublic', 'true');

    const baseUrl = import.meta.env.VITE_SIMPLE_REST_URL;
    const accessToken = localStorage.getItem('accessToken');

    const response = await fetch(`${baseUrl}/files`, {
      method: 'POST',
      headers: {
        Authorization: `Bearer ${accessToken}`,
      },
      body: formData,
    });

    if (!response.ok) {
      throw new Error(`Upload failed: ${response.statusText}`);
    }

    return response.json();
  };

  const updateEquipmentPhoto = async (fileId: number) => {
    const baseUrl = import.meta.env.VITE_SIMPLE_REST_URL;
    const accessToken = localStorage.getItem('accessToken');

    const response = await fetch(`${baseUrl}/equipment/${vehicleId}`, {
      method: 'PATCH',
      headers: {
        Authorization: `Bearer ${accessToken}`,
        'Content-Type': 'application/json',
      },
      body: JSON.stringify({
        primaryPhotoFileId: fileId,
      }),
    });

    if (!response.ok) {
      throw new Error(`Failed to update equipment: ${response.statusText}`);
    }

    return response.json();
  };

  const createEntityImage = async (fileId: number) => {
    const baseUrl = import.meta.env.VITE_SIMPLE_REST_URL;
    const accessToken = localStorage.getItem('accessToken');

    const response = await fetch(`${baseUrl}/equipment/images`, {
      method: 'POST',
      headers: {
        Authorization: `Bearer ${accessToken}`,
        'Content-Type': 'application/json',
      },
      body: JSON.stringify({
        entityType: 'equipment',
        entityId: vehicleId,
        fileId: fileId,
        imageType: 'VEHICLE',
        caption: 'Primary vehicle photo',
        displayOrder: 0,
      }),
    });

    if (!response.ok) {
      throw new Error(`Failed to create entity image: ${response.statusText}`);
    }

    return response.json();
  };

  const handleUpload = async () => {
    if (!selectedFile) {
      return;
    }

    setUploading(true);
    try {
      // Upload file to server
      const fileData = await uploadToServer(selectedFile);

      // Update equipment with primary photo file ID
      await updateEquipmentPhoto(fileData.id);

      // Create entity image record
      await createEntityImage(fileData.id);

      // Update UI
      onPhotoUpdated(fileData.url);
      notify('Photo uploaded successfully', { type: 'success' });
      refresh();
      onClose();
    } catch (error: any) {
      notify(`Failed to upload photo: ${error.message}`, { type: 'error' });
    } finally {
      setUploading(false);
    }
  };

  const handleRemovePhoto = async () => {
    setUploading(true);
    try {
      // Update equipment to remove primary photo
      await updateEquipmentPhoto(null);

      setSelectedFile(null);
      setPreviewUrl('');
      onPhotoUpdated('');
      notify('Photo removed', { type: 'success' });
      refresh();
      onClose();
    } catch (error: any) {
      notify(`Failed to remove photo: ${error.message}`, { type: 'error' });
    } finally {
      setUploading(false);
    }
  };

  return (
    <Dialog open={open} onClose={onClose} maxWidth="sm" fullWidth>
      <DialogTitle>
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
          Vehicle Photo
          <IconButton onClick={onClose}>
            <CloseIcon />
          </IconButton>
        </Box>
      </DialogTitle>
      <DialogContent>
        <Box sx={{ textAlign: 'center', py: 2 }}>
          {previewUrl ? (
            <Box
              component="img"
              src={previewUrl}
              alt="Vehicle preview"
              sx={{
                maxWidth: '100%',
                maxHeight: 300,
                borderRadius: 2,
                mb: 2,
              }}
            />
          ) : (
            <Box
              onDragOver={handleDragOver}
              onDragLeave={handleDragLeave}
              onDrop={handleDrop}
              onClick={() => document.getElementById('photo-file-input')?.click()}
              sx={{
                width: '100%',
                height: 200,
                border: isDragOver ? '2px dashed #1976d2' : '2px dashed #ccc',
                borderRadius: 2,
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                mb: 2,
                backgroundColor: isDragOver ? '#f3f7ff' : '#f9f9f9',
                cursor: 'pointer',
                transition: 'all 0.2s ease',
                '&:hover': {
                  borderColor: '#1976d2',
                  backgroundColor: '#f3f7ff',
                },
              }}
            >
              <Box sx={{ textAlign: 'center', color: 'text.secondary' }}>
                <UploadIcon
                  sx={{ fontSize: 48, mb: 1, color: isDragOver ? '#1976d2' : 'inherit' }}
                />
                <Typography variant="body2" sx={{ color: isDragOver ? '#1976d2' : 'inherit' }}>
                  {isDragOver ? 'Drop photo here' : 'Drag and drop photo here or click to select'}
                </Typography>
              </Box>
            </Box>
          )}

          <input
            accept="image/*"
            style={{ display: 'none' }}
            id="photo-file-input"
            type="file"
            onChange={handleInputChange}
          />
          <label htmlFor="photo-file-input">
            <MuiButton variant="outlined" component="span" startIcon={<PhotoIcon />} sx={{ mr: 1 }}>
              Select Photo
            </MuiButton>
          </label>

          {currentPhoto && (
            <MuiButton variant="outlined" color="error" onClick={handleRemovePhoto} sx={{ ml: 1 }}>
              Remove Current Photo
            </MuiButton>
          )}

          <Typography variant="caption" display="block" color="text.secondary" sx={{ mt: 2 }}>
            Maximum file size: 5MB • Supported formats: JPG, PNG, WebP
          </Typography>
        </Box>
      </DialogContent>
      <DialogActions>
        <MuiButton onClick={onClose}>Cancel</MuiButton>
        <MuiButton variant="contained" onClick={handleUpload} disabled={!selectedFile || uploading}>
          {uploading ? 'Uploading...' : 'Upload Photo'}
        </MuiButton>
      </DialogActions>
    </Dialog>
  );
};

// Vehicle Summary Card Component
const VehicleSummaryCard = () => {
  const record = useRecordContext();
  const [photoDialogOpen, setPhotoDialogOpen] = useState(false);
  const [currentPhoto, setCurrentPhoto] = useState(record?.primaryPhotoFile?.url || '');

  // Update photo when record changes
  React.useEffect(() => {
    setCurrentPhoto(record?.primaryPhotoFile?.url || '');
  }, [record?.primaryPhotoFile?.url]);

  if (!record) {
    return null;
  }

  const handlePhotoUpdated = (photoUrl: string) => {
    setCurrentPhoto(photoUrl);
  };

  return (
    <>
      <Card sx={{ mb: 3 }}>
        <CardContent>
          <Grid container spacing={3} alignItems="center">
            {/* Vehicle Photo */}
            <Grid item xs={12} md={3}>
              <Box
                sx={{
                  width: '100%',
                  height: 180,
                  borderRadius: 2,
                  overflow: 'hidden',
                  backgroundColor: '#f5f5f5',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  cursor: 'pointer',
                  border: '2px dashed #ddd',
                  '&:hover': {
                    backgroundColor: '#f0f0f0',
                    borderColor: '#1976d2',
                  },
                }}
                onClick={() => setPhotoDialogOpen(true)}
              >
                {currentPhoto ? (
                  <Box
                    component="img"
                    src={currentPhoto}
                    alt={`${record.name} photo`}
                    sx={{
                      width: '100%',
                      height: '100%',
                      objectFit: 'cover',
                    }}
                  />
                ) : (
                  <Box sx={{ textAlign: 'center', color: 'text.secondary' }}>
                    <PhotoIcon sx={{ fontSize: 48, mb: 1 }} />
                    <Typography variant="body2">Click to add photo</Typography>
                  </Box>
                )}
              </Box>
            </Grid>

            {/* Vehicle Info */}
            <Grid item xs={12} md={9}>
              <Typography variant="h4" fontWeight="bold" gutterBottom>
                {record.name}
              </Typography>
              <Box sx={{ display: 'flex', gap: 1, mb: 2 }}>
                <TypeField />
                <StatusField />
                <OwnershipField />
              </Box>
              <Grid container spacing={2}>
                <Grid item xs={6}>
                  <Typography variant="body2" color="text.secondary">
                    Year • Make • Model
                  </Typography>
                  <Typography variant="body1" fontWeight="medium">
                    {record.manufacturedYear || '-'} • {record.make || '-'} • {record.model || '-'}
                  </Typography>
                </Grid>
                <Grid item xs={6}>
                  <Typography variant="body2" color="text.secondary">
                    License Plate
                  </Typography>
                  <Typography variant="body1" fontWeight="medium">
                    {record.licensePlate || '-'}
                  </Typography>
                </Grid>
                <Grid item xs={6}>
                  <Typography variant="body2" color="text.secondary">
                    VIN
                  </Typography>
                  <Typography variant="body1" fontWeight="medium">
                    {record.vin || '-'}
                  </Typography>
                </Grid>
                <Grid item xs={6}>
                  <Typography variant="body2" color="text.secondary">
                    Transmission
                  </Typography>
                  <Typography variant="body1" fontWeight="medium">
                    {record.transmission || '-'}
                  </Typography>
                </Grid>
              </Grid>
            </Grid>
          </Grid>
        </CardContent>

        <PhotoUploadDialog
          open={photoDialogOpen}
          onClose={() => setPhotoDialogOpen(false)}
          vehicleId={record.id}
          currentPhoto={currentPhoto}
          onPhotoUpdated={handlePhotoUpdated}
        />
      </Card>
    </>
  );
};

export const EquipmentShow = () => (
  <Show actions={<EquipmentShowActions />}>
    <ResourceBreadcrumbs />
    <VehicleSummaryCard />
    <TabbedShowLayout>
      <Tab label="Overview" icon={<InfoIcon />}>
        <Box sx={{ maxWidth: 800, mx: 'auto', p: 3 }}>
          {/* Identification Section */}
          <Card
            sx={{
              mb: 3,
              overflow: 'visible',
              border: '1px solid #e0e0e0',
              boxShadow: '0 1px 2px rgba(0,0,0,0.05)',
            }}
          >
            <CardContent>
              <Box sx={{ display: 'flex', alignItems: 'center', mb: 3 }}>
                <VehicleIcon sx={{ fontSize: 28, color: '#1976d2', mr: 2 }} />
                <Typography variant="h5" fontWeight="bold" color="primary">
                  Vehicle Identification
                </Typography>
              </Box>
              <Grid container spacing={3}>
                <Grid item xs={12} sm={6}>
                  <Box sx={{ p: 2, backgroundColor: '#f8f9fa', borderRadius: 2 }}>
                    <Typography variant="body2" color="text.secondary" fontWeight="medium">
                      VIN
                    </Typography>
                    <Typography variant="body1" sx={{ mt: 0.5, wordBreak: 'break-all' }}>
                      <SafeTextField source="vin" />
                    </Typography>
                  </Box>
                </Grid>
                <Grid item xs={12} sm={6}>
                  <Box sx={{ p: 2, backgroundColor: '#f8f9fa', borderRadius: 2 }}>
                    <Typography variant="body2" color="text.secondary" fontWeight="medium">
                      License Plate
                    </Typography>
                    <Typography variant="body1" sx={{ mt: 0.5 }}>
                      <SafeTextField source="licensePlate" />
                    </Typography>
                  </Box>
                </Grid>
                <Grid item xs={12} sm={6}>
                  <Box sx={{ p: 2, backgroundColor: '#f8f9fa', borderRadius: 2 }}>
                    <Typography variant="body2" color="text.secondary" fontWeight="medium">
                      Max Weight (kg)
                    </Typography>
                    <Typography variant="body1" sx={{ mt: 0.5 }}>
                      <SafeNumberField source="maxHauledKg" />
                    </Typography>
                  </Box>
                </Grid>
              </Grid>
            </CardContent>
          </Card>

          {/* Specifications Section */}
          <Card
            sx={{ mb: 3, border: '1px solid #e0e0e0', boxShadow: '0 1px 2px rgba(0,0,0,0.05)' }}
          >
            <CardContent>
              <Box sx={{ display: 'flex', alignItems: 'center', mb: 3 }}>
                <InfoIcon sx={{ fontSize: 28, color: '#1976d2', mr: 2 }} />
                <Typography variant="h5" fontWeight="bold" color="primary">
                  Specifications
                </Typography>
              </Box>
              <Grid container spacing={3}>
                <Grid item xs={12} sm={4}>
                  <Box sx={{ p: 2, backgroundColor: '#f8f9fa', borderRadius: 2 }}>
                    <Typography variant="body2" color="text.secondary" fontWeight="medium">
                      Year
                    </Typography>
                    <Typography variant="h6" sx={{ mt: 0.5 }}>
                      <SafeTextField source="manufacturedYear" />
                    </Typography>
                  </Box>
                </Grid>
                <Grid item xs={12} sm={4}>
                  <Box sx={{ p: 2, backgroundColor: '#f8f9fa', borderRadius: 2 }}>
                    <Typography variant="body2" color="text.secondary" fontWeight="medium">
                      Make
                    </Typography>
                    <Typography variant="h6" sx={{ mt: 0.5 }}>
                      <SafeTextField source="make" />
                    </Typography>
                  </Box>
                </Grid>
                <Grid item xs={12} sm={4}>
                  <Box sx={{ p: 2, backgroundColor: '#f8f9fa', borderRadius: 2 }}>
                    <Typography variant="body2" color="text.secondary" fontWeight="medium">
                      Model
                    </Typography>
                    <Typography variant="h6" sx={{ mt: 0.5 }}>
                      <SafeTextField source="model" />
                    </Typography>
                  </Box>
                </Grid>
                <Grid item xs={12} sm={6}>
                  <Box sx={{ p: 2, backgroundColor: '#f8f9fa', borderRadius: 2 }}>
                    <Typography variant="body2" color="text.secondary" fontWeight="medium">
                      Transmission
                    </Typography>
                    <Typography variant="body1" sx={{ mt: 0.5 }}>
                      <SafeTextField source="transmission" />
                    </Typography>
                  </Box>
                </Grid>
              </Grid>
            </CardContent>
          </Card>

          {/* Financial Information */}
          <Card
            sx={{ mb: 3, border: '1px solid #e0e0e0', boxShadow: '0 1px 2px rgba(0,0,0,0.05)' }}
          >
            <CardContent>
              <Box sx={{ display: 'flex', alignItems: 'center', mb: 3 }}>
                <InfoIcon sx={{ fontSize: 28, color: '#1976d2', mr: 2 }} />
                <Typography variant="h5" fontWeight="bold" color="primary">
                  Financial Information
                </Typography>
              </Box>
              <Grid container spacing={3}>
                <Grid item xs={12} sm={4}>
                  <Box sx={{ p: 2, backgroundColor: '#f8f9fa', borderRadius: 2 }}>
                    <Typography variant="body2" color="text.secondary" fontWeight="medium">
                      Acquisition Date
                    </Typography>
                    <Typography variant="h6" sx={{ mt: 0.5 }}>
                      <FunctionField
                        source="acquireDate"
                        render={(record: any) => {
                          if (!record.acquireDate) {
                            return '-';
                          }
                          return new Date(record.acquireDate).toLocaleDateString('en-US', { timeZone: 'UTC' });
                        }}
                      />
                    </Typography>
                  </Box>
                </Grid>
                <Grid item xs={12} sm={4}>
                  <Box sx={{ p: 2, backgroundColor: '#f8f9fa', borderRadius: 2 }}>
                    <Typography variant="body2" color="text.secondary" fontWeight="medium">
                      List Price New
                    </Typography>
                    <Typography variant="h6" sx={{ mt: 0.5 }}>
                      <CurrencyField source="listPriceNew" />
                    </Typography>
                  </Box>
                </Grid>
                <Grid item xs={12} sm={4}>
                  <Box sx={{ p: 2, backgroundColor: '#f8f9fa', borderRadius: 2 }}>
                    <Typography variant="body2" color="text.secondary" fontWeight="medium">
                      Actual Cash Value
                    </Typography>
                    <Typography variant="h6" sx={{ mt: 0.5 }}>
                      <CurrencyField source="actualCashValue" />
                    </Typography>
                  </Box>
                </Grid>
              </Grid>
            </CardContent>
          </Card>

          {/* Job Types Section */}
          <JobTypesSection />

          {/* Commodities Section */}
          <CommoditiesSection />

          {/* Notes Section */}
          <Card
            sx={{ mb: 3, border: '1px solid #e0e0e0', boxShadow: '0 1px 2px rgba(0,0,0,0.05)' }}
          >
            <CardContent>
              <Typography variant="h6" gutterBottom color="primary" sx={{ mb: 2 }}>
                Additional Notes
              </Typography>
              <Box
                sx={{
                  p: 2,
                  backgroundColor: '#f5f5f5',
                  borderRadius: 2,
                  minHeight: 60,
                  border: '1px solid #e0e0e0',
                }}
              >
                <SafeTextField source="notes" />
              </Box>
            </CardContent>
          </Card>

          {/* Documents Section */}
          <DocumentsSectionViewOnly />
        </Box>
      </Tab>

      <Tab
        label={<TabLabelWithCount title="Tasks" color="#9c27b0" />}
        icon={<TaskIcon sx={{ color: '#9c27b0' }} />}
      >
        <TasksTab />
      </Tab>

      <Tab
        label={<TabLabelWithCount title="Maintenance" color="#ed6c02" />}
        icon={<MaintenanceIcon sx={{ color: '#ed6c02' }} />}
      >
        <MaintenanceTab />
      </Tab>

      <Tab
        label={<TabLabelWithCount title="Fuel Records" color="#0288d1" />}
        icon={<FuelIcon sx={{ color: '#0288d1' }} />}
      >
        <FuelTab />
      </Tab>

      <Tab
        label={<TabLabelWithCount title="Inspections" color="#2e7d32" />}
        icon={<InspectionIcon sx={{ color: '#2e7d32' }} />}
      >
        <InspectionsTab />
      </Tab>

      <Tab
        label={<TabLabelWithCount title="Assignments" color="#1976d2" />}
        icon={<AssignmentIcon sx={{ color: '#1976d2' }} />}
      >
        <AssignmentsTab />
      </Tab>

      <Tab
        label={<TabLabelWithCount title="Incidents" color="#d32f2f" />}
        icon={<IncidentIcon sx={{ color: '#d32f2f' }} />}
      >
        <IncidentsTab />
      </Tab>
    </TabbedShowLayout>
  </Show>
);

export default EquipmentShow;
